// Screen 12 — Account-request queue.
//
// The inbox of account requests across all tenants. Each row shows the request
// + client (resolved from /admin/clients), the source channel (portal /
// telegram / sheet), the geo, the type, the quantity, an Assign action (the
// first transition out of `pending`), a Stepper of the state machine, and an
// Advance control that fires the next transition. Assign opens a modal that
// surfaces geo-matched agencies first (from /admin/agencies); broadcasting to
// ALL agencies is a separate, DELIBERATE option (an empty body — the backend
// treats absent agency_ids as broadcast).
//
// REAL SHAPES (admin Go handlers — source of truth):
//   GET  /admin/account-requests?status=&limit=&offset=
//        -> { account_requests: AdminAccountRequest[] }
//   POST /admin/account-requests/{id}/assign  body {"agency_ids":[...]} —
//        PLURAL, the admin's explicit routing choice; {} = broadcast to ALL
//   POST /admin/account-requests/{id}/{in-progress|ready|share|reject}
//   POST /admin/account-requests/{id}/deliver      (batch advertiser ids)
//   POST /admin/accounts/{id}/attribution          (re-attribute a delivered row)
//   POST /admin/accounts/{id}/unlink               (detach a row from its request)
// The share transition is GUARDED: unforced shares on an under-delivered
// request 409 with {reason:"undelivered_qty"} and need an explicit force
// confirmation. The request state machine: pending → assigned → in_progress →
// ready → shared.
// Rejected is a terminal off-ramp surfaced as a danger pill (not a stepper
// position). The backend row carries client_id / request_type / qty / status /
// geo / source — there is NO client name or agency name on the row, so we
// resolve client names from /admin/clients and agency names from /admin/agencies.

import { useCallback, useEffect, useId, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/api/client";
import {
  deliverAccounts,
  getAccountRequestDetail,
  reopenAccountRequest,
  shareAccountRequest,
  type AccountRequestDetail,
  type AccountRow,
  type DeliverRefusal,
  type ReopenFullyDelivered,
} from "@/api/accounts";
import { listOpsChats } from "@/api/telegram";
import { errMessage } from "@/lib/errMessage";
import { cn } from "@/lib/utils";
import { AdminShell } from "@/components/nexus/AdminShell";
import {
  AttributionUnlinkControls,
  DataTable,
  Drawer,
  Modal,
  Stepper,
  ToneBadge,
  type Column,
  type Step,
  type Tone,
} from "@/components/nexus";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { NativeSelect, Pager } from "./controls";

// The admin queue row, exactly as the backend serializes it.
interface AccountRequest {
  id: string;
  client_id: string;
  geo: string;
  request_type: string;
  qty: number;
  status: RequestState;
  source: string;
  notes?: string;
  // Client's BC id / email to share delivered accounts to; "" when uncaptured.
  share_target?: string;
}

type RequestState =
  | "pending"
  | "assigned"
  | "in_progress"
  | "ready"
  | "shared"
  | "rejected";

const STEPS: Step[] = [
  { key: "pending", label: "Pending" },
  { key: "assigned", label: "Assigned" },
  { key: "in_progress", label: "In progress" },
  { key: "ready", label: "Ready" },
  { key: "shared", label: "Shared" },
];

// Map a state to its stepper index and the transition that advances it. The
// first step out of `pending` is the special Assign action (a separate modal).
const FLOW: Record<
  RequestState,
  { index: number; next?: { transition: string; label: string } }
> = {
  pending: { index: 0 }, // advanced via Assign (special-cased)
  assigned: { index: 1, next: { transition: "in-progress", label: "Start" } },
  in_progress: { index: 2, next: { transition: "ready", label: "Mark ready" } },
  ready: { index: 3, next: { transition: "share", label: "Share" } },
  shared: { index: 4 },
  rejected: { index: 0 },
};

// Safe lookup — an unexpected/new status never crashes the stepper cell.
function flowOf(state: RequestState): { index: number; next?: { transition: string; label: string } } {
  return FLOW[state] ?? { index: 0 };
}

// Terminal states the default "Open" view hides (toggle to show).
const TERMINAL_STATES: readonly string[] = ["shared", "rejected"];

// Every backend status, for the explicit server-side ?status filter.
const ALL_STATUSES: readonly RequestState[] = [
  "pending",
  "assigned",
  "in_progress",
  "ready",
  "shared",
  "rejected",
];

const PAGE_SIZE = 50;

interface ClientRef {
  id: string;
  name: string;
}
interface AgencyRef {
  id: string;
  name: string;
  geos: string[];
}

function sourceTone(source: string): Tone {
  switch (source) {
    case "portal":
      return "brand";
    case "telegram":
      return "live";
    case "sheet":
      return "stale";
    default:
      return "na";
  }
}

export function AccountRequestsPage() {
  const [requests, setRequests] = useState<AccountRequest[]>([]);
  const [clients, setClients] = useState<ClientRef[]>([]);
  const [agencies, setAgencies] = useState<AgencyRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [assignFor, setAssignFor] = useState<AccountRequest | null>(null);
  const [detailFor, setDetailFor] = useState<AccountRequest | null>(null);
  // Agency ids with an ACTIVE agency Telegram chat binding (from
  // /admin/ops-chats). null = the lookup failed / not yet loaded — the Assign
  // modal then omits the chat-bound badges rather than falsely claiming
  // "no chat bound". Data-only; assigning to an unbound agency is never blocked.
  const [boundAgencies, setBoundAgencies] = useState<Set<string> | null>(null);
  // The 409 undelivered_qty share guard: the backend refused an unforced share
  // because delivered < qty — the operator must explicitly force it.
  const [shareGuard, setShareGuard] = useState<{
    request: AccountRequest;
    delivered: number;
    qty: number;
  } | null>(null);
  // Detail cache keyed by request id — the LIST shape has no delivered count,
  // so the Delivered column shows the count only once a detail was fetched
  // (drawer open). Deliberately NOT an N+1 per-row fetch.
  const [details, setDetails] = useState<Record<string, AccountRequestDetail>>(
    {},
  );

  // Status filter + offset pager (server-side ?status/?limit/?offset). "open"
  // fetches unfiltered and hides terminal rows client-side (the backend takes
  // one exact status, not a negated set); "all" shows everything.
  const [statusF, setStatusF] = useState<string>("open");
  const [offset, setOffset] = useState(0);

  function clientName(id: string): string {
    return clients.find((c) => c.id === id)?.name ?? id ?? "—";
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The queue is the critical fetch; clients/agencies are name lookups that
      // must not blank the screen if one is slow — fetch them tolerantly.
      const qs = new URLSearchParams();
      if (statusF !== "open" && statusF !== "all") qs.set("status", statusF);
      qs.set("limit", String(PAGE_SIZE));
      qs.set("offset", String(offset));
      const res = await api.get<{ account_requests: AccountRequest[] }>(
        `/admin/account-requests?${qs.toString()}`,
      );
      setRequests(res.account_requests ?? []);
      const [c, a] = await Promise.allSettled([
        api.get<{ clients: ClientRef[] }>("/admin/clients"),
        api.get<{ agencies: AgencyRef[] }>("/admin/agencies"),
      ]);
      if (c.status === "fulfilled") setClients(c.value.clients ?? []);
      if (a.status === "fulfilled") setAgencies(a.value.agencies ?? []);
    } catch (err) {
      setError(errMessage(err));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusF, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  // Agency chat-binding lookup (tolerant — a failure leaves boundAgencies null,
  // so the Assign modal simply omits the chat badges/warning). Loaded once.
  useEffect(() => {
    listOpsChats()
      .then((res) => {
        const set = new Set<string>();
        for (const c of res.chats ?? []) {
          if (c.active && c.kind === "agency" && c.agency_id) {
            set.add(c.agency_id);
          }
        }
        setBoundAgencies(set);
      })
      .catch(() => setBoundAgencies(null));
  }, []);

  // Deep-link support: the Accounts hub (and its drawer) link a delivered row's
  // request as /account-requests?request=<id>. When that request is in the
  // loaded page, open its detail drawer once, then drop the param so a manual
  // close does not immediately re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("request");
    if (!openId || detailFor) return;
    const match = requests.find((r) => r.id === openId);
    if (!match) return;
    setDetailFor(match);
    const next = new URLSearchParams(searchParams);
    next.delete("request");
    setSearchParams(next, { replace: true });
  }, [requests, searchParams, detailFor, setSearchParams]);

  // The default "Open" view hides terminal rows CLIENT-SIDE within the fetched
  // page (the pager still walks the server offset over the unfiltered rows).
  const visibleRequests =
    statusF === "open"
      ? requests.filter((r) => !TERMINAL_STATES.includes(r.status))
      : requests;

  async function advance(req: AccountRequest) {
    const next = flowOf(req.status).next;
    if (!next) return;
    setBusy(req.id);
    try {
      if (next.transition === "share") {
        // The share transition is GUARDED server-side: an unforced share on an
        // under-delivered request 409s with {reason:"undelivered_qty"} — we
        // surface it as an explicit force-share confirmation.
        const res = await shareAccountRequest(req.id);
        if (!res.ok) {
          setShareGuard({ request: req, delivered: res.delivered, qty: res.qty });
          return;
        }
      } else {
        await api.post(`/admin/account-requests/${req.id}/${next.transition}`);
      }
      toast.success(`${clientName(req.client_id)}: ${next.label.toLowerCase()} done`);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function forceShare(req: AccountRequest) {
    setBusy(req.id);
    try {
      const res = await shareAccountRequest(req.id, { force: true });
      if (res.ok) {
        toast.success(`${clientName(req.client_id)}: force-shared`);
      }
      setShareGuard(null);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function reject(req: AccountRequest) {
    setBusy(req.id);
    try {
      await api.post(`/admin/account-requests/${req.id}/reject`);
      toast.success(`${clientName(req.client_id)}: request rejected`);
      await load();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(null);
    }
  }

  const columns: Column<AccountRequest>[] = [
    {
      key: "request",
      header: "Request",
      cell: (r) => (
        <div className="leading-tight">
          <div className="font-mono text-xs text-nx-text-3 tabular-nums">
            {r.id}
          </div>
          <div className="font-medium text-nx-text">
            {r.client_id ? clientName(r.client_id) : "Unassigned"}
          </div>
        </div>
      ),
    },
    {
      key: "source",
      header: "Source",
      cell: (r) => (
        <ToneBadge tone={sourceTone(r.source)} className="capitalize">
          {r.source || "—"}
        </ToneBadge>
      ),
    },
    {
      key: "geo",
      header: "Geo",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2">{r.geo || "—"}</span>
      ),
    },
    {
      key: "type",
      header: "Type",
      cell: (r) => <span className="text-nx-text-2">{r.request_type}</span>,
    },
    {
      key: "share_target",
      header: "Share to",
      cell: (r) =>
        r.share_target ? (
          <span
            className="block max-w-[12rem] truncate text-xs text-nx-text-2"
            title={r.share_target}
          >
            {r.share_target}
          </span>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "qty",
      header: "Qty",
      align: "right",
      cell: (r) => (
        <span className="font-mono text-xs text-nx-text-2 tabular-nums">
          {r.qty}
        </span>
      ),
    },
    {
      key: "delivered",
      header: "Delivered",
      cell: (r) => {
        const d = details[r.id];
        const qty = d ? d.qty : Math.max(r.qty, 1);
        // A "shared" request whose known delivered count is short of qty is a
        // legacy/forced share — surface the data mismatch subtly but visibly.
        const mismatch =
          r.status === "shared" && d !== undefined && d.total_delivered < d.qty;
        return (
          <span className="inline-flex items-center gap-1">
            <Button
              variant="ghost"
              size="xs"
              aria-label={`Delivery details ${r.id}`}
              className="font-mono text-xs tabular-nums"
              onClick={() => setDetailFor(r)}
            >
              {d ? `${d.total_delivered}/${qty}` : `—/${qty}`}
            </Button>
            {mismatch && d && (
              <span
                role="img"
                aria-label={`Delivery mismatch ${r.id}`}
                title={`Shared with only ${d.total_delivered}/${d.qty} delivered`}
              >
                <TriangleAlert className="size-3.5 text-nx-warn" />
              </span>
            )}
          </span>
        );
      },
    },
    {
      key: "assign",
      header: "Assign",
      cell: (r) =>
        r.status === "pending" ? (
          <Button variant="outline" size="xs" onClick={() => setAssignFor(r)}>
            Assign…
          </Button>
        ) : (
          <span className="text-nx-text-3">—</span>
        ),
    },
    {
      key: "stepper",
      header: "State",
      cell: (r) =>
        r.status === "rejected" ? (
          <ToneBadge tone="danger">Rejected</ToneBadge>
        ) : (
          <Stepper steps={STEPS} current={flowOf(r.status).index} />
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => {
        const next = flowOf(r.status).next;
        if (r.status === "rejected" || r.status === "shared") {
          return <span className="text-nx-text-3">—</span>;
        }
        return (
          <div className="flex justify-end gap-2">
            {next ? (
              <Button
                size="xs"
                disabled={busy === r.id}
                onClick={() => advance(r)}
              >
                {next.label}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="xs"
              disabled={busy === r.id}
              onClick={() => reject(r)}
              className="text-nx-debit hover:text-nx-debit"
            >
              Reject
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <AdminShell>
      <div className="mb-5">
        <h2 className="font-heading text-lg font-semibold">
          Account-request queue
        </h2>
        <p className="mt-1 max-w-2xl text-sm text-nx-text-2">
          Cross-tenant inbox. Assign a geo-matched agency, then advance through
          the state machine.
        </p>
      </div>

      {/* Status filter — "Open" (default) hides terminal shared/rejected rows;
          a specific status is applied SERVER-SIDE via ?status. */}
      <div className="mb-4 max-w-xs">
        <NativeSelect
          aria-label="Filter by status"
          value={statusF}
          onChange={(e) => {
            setStatusF(e.target.value);
            setOffset(0);
          }}
        >
          <option value="open">Open (hide shared/rejected)</option>
          <option value="all">All statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.replace("_", " ")}
            </option>
          ))}
        </NativeSelect>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>Could not load the queue</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={visibleRequests}
            rowKey={(r) => r.id}
            empty="The queue is empty."
          />
          <Pager
            offset={offset}
            pageSize={PAGE_SIZE}
            count={requests.length}
            loading={loading}
            onOffset={setOffset}
          />
        </>
      )}

      <AssignModal
        request={assignFor}
        agencies={agencies}
        boundAgencies={boundAgencies}
        clientName={clientName}
        onOpenChange={(o) => {
          if (!o) setAssignFor(null);
        }}
        onAssigned={async () => {
          setAssignFor(null);
          await load();
        }}
      />

      {/* Force-share confirm — the backend's 409 undelivered_qty guard made
          the mismatch explicit; sharing anyway is audited with force:true. */}
      <Modal
        open={shareGuard !== null}
        onOpenChange={(o) => {
          if (!o) setShareGuard(null);
        }}
        title="Share an under-delivered request?"
        description={
          shareGuard
            ? `${clientName(shareGuard.request.client_id)} · only ${shareGuard.delivered} of ${shareGuard.qty} accounts delivered`
            : undefined
        }
        footer={
          <>
            <Button variant="outline" onClick={() => setShareGuard(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={shareGuard !== null && busy === shareGuard.request.id}
              onClick={() => shareGuard && void forceShare(shareGuard.request)}
            >
              Force share
            </Button>
          </>
        }
      >
        <p className="text-sm text-nx-text-2">
          The backend refused the share because fewer accounts are delivered
          than requested. Forcing marks the request shared anyway (audited);
          the normal path is delivering the remaining accounts first.
        </p>
      </Modal>

      <RequestDetailDrawer
        request={detailFor}
        agencies={agencies}
        clientName={clientName}
        onOpenChange={(o) => {
          if (!o) setDetailFor(null);
        }}
        onDetail={(id, d) => setDetails((prev) => ({ ...prev, [id]: d }))}
        onDelivered={async () => {
          await load();
        }}
      />
    </AdminShell>
  );
}

// The sentinel radio value for "broadcast to all agencies" — a deliberate
// operator choice, posted as an EMPTY body (the backend treats absent
// agency_ids as broadcast; routing="broadcast").
const BROADCAST = "__broadcast__";

function AssignModal({
  request,
  agencies,
  boundAgencies,
  clientName,
  onOpenChange,
  onAssigned,
}: {
  request: AccountRequest | null;
  agencies: AgencyRef[];
  // Agency ids with an active Telegram chat binding; null = unknown (badges +
  // warning suppressed).
  boundAgencies: Set<string> | null;
  clientName: (id: string) => string;
  onOpenChange: (open: boolean) => void;
  onAssigned: () => void | Promise<void>;
}) {
  const [selected, setSelected] = useState<string>("");
  const [busy, setBusy] = useState(false);

  // Re-seed the selection whenever a new request opens: pre-pick the first
  // geo-matched agency for the operator; with no agencies on file the only
  // valid choice is the broadcast.
  useEffect(() => {
    if (!request) return;
    const match = agencies.find((a) => a.geos?.includes(request.geo));
    setSelected(match?.id ?? agencies[0]?.id ?? BROADCAST);
  }, [request, agencies]);

  if (!request) {
    return <Modal open={false} onOpenChange={onOpenChange} title="Assign agency" />;
  }

  // Geo-matched agencies first, then the rest.
  const matched = agencies.filter((a) => a.geos?.includes(request.geo));
  const others = agencies.filter((a) => !a.geos?.includes(request.geo));
  const ordered = [...matched, ...others];

  const agencyNameOf = (id: string) =>
    agencies.find((a) => a.id === id)?.name ?? id;

  // Non-blocking notification warning: the assign/broadcast targets that have
  // NO bound Telegram chat and therefore won't be notified. Suppressed entirely
  // when the binding lookup is unavailable (boundAgencies === null).
  const unboundTargets =
    boundAgencies === null
      ? []
      : selected === BROADCAST
        ? agencies.filter((a) => !boundAgencies.has(a.id))
        : selected && !boundAgencies.has(selected)
          ? agencies.filter((a) => a.id === selected)
          : [];

  async function assign() {
    if (!selected || !request) return;
    setBusy(true);
    try {
      // The backend contract is PLURAL: {"agency_ids":[...]} routes the
      // request to exactly those agencies; an EMPTY body broadcasts to ALL.
      const body =
        selected === BROADCAST ? {} : { agency_ids: [selected] };
      await api.post(`/admin/account-requests/${request.id}/assign`, body);
      toast.success(
        selected === BROADCAST
          ? "Request broadcast to all agencies"
          : "Agency assigned",
      );
      await onAssigned();
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={true}
      onOpenChange={onOpenChange}
      title="Assign agency"
      description={`${request.client_id ? clientName(request.client_id) : "Unassigned"} · ${request.geo || "—"} · ${request.qty}× ${request.request_type}`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!selected || busy} onClick={assign}>
            {selected === BROADCAST ? "Broadcast" : "Assign"}
          </Button>
        </>
      }
    >
      <fieldset className="grid gap-2">
        <legend className="sr-only">Choose an agency or broadcast</legend>
        {ordered.length === 0 && (
          <p className="text-sm text-nx-text-2">
            No agencies on file yet — you can still broadcast (the request is
            accepted unrouted), or add an agency in CRM first.
          </p>
        )}
        {ordered.map((a) => {
          const isMatch = a.geos?.includes(request.geo);
          return (
            <label
              key={a.id}
              className="flex cursor-pointer items-center gap-3 rounded-md border border-nx-border bg-nx-bg-2 px-3 py-2 hover:border-nx-brand/40 has-checked:border-nx-brand has-checked:bg-nx-brand-soft"
            >
              <input
                type="radio"
                name="agency"
                value={a.id}
                checked={selected === a.id}
                onChange={() => setSelected(a.id)}
                className="accent-nx-brand"
              />
              <span className="flex-1">
                <span className="font-medium text-nx-text">{a.name}</span>
                <span className="ml-2 text-xs text-nx-text-3">
                  {(a.geos ?? []).join(", ")}
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-1.5">
                {isMatch && <ToneBadge tone="credit">Geo match</ToneBadge>}
                {boundAgencies !== null &&
                  (boundAgencies.has(a.id) ? (
                    <ToneBadge tone="credit">chat bound</ToneBadge>
                  ) : (
                    <ToneBadge tone="warn">no chat bound</ToneBadge>
                  ))}
              </span>
            </label>
          );
        })}
        {/* Broadcast is a deliberate, visually separated choice — never the
            silent default of a missing field. */}
        <label className="mt-1 flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-nx-border bg-nx-bg-2 px-3 py-2 hover:border-nx-brand/40 has-checked:border-nx-brand has-checked:bg-nx-brand-soft">
          <input
            type="radio"
            name="agency"
            value={BROADCAST}
            checked={selected === BROADCAST}
            onChange={() => setSelected(BROADCAST)}
            className="accent-nx-brand"
          />
          <span className="flex-1">
            <span className="font-medium text-nx-text">
              Broadcast to all agencies
            </span>
            <span className="block text-xs text-nx-text-3">
              No explicit routing — every agency receives this request.
            </span>
          </span>
        </label>
      </fieldset>

      {/* Non-blocking chat-binding warning — the target(s) with no bound
          Telegram chat won't be notified. Never disables the assign action. */}
      {unboundTargets.length > 0 && (
        <div
          role="status"
          className="mt-3 grid gap-1 rounded-md bg-nx-warn-soft/60 px-3 py-2 text-xs text-nx-text-2"
        >
          <p className="flex items-center gap-1.5 font-medium text-nx-warn">
            <TriangleAlert className="size-3.5 shrink-0" />
            {selected === BROADCAST
              ? `${unboundTargets.length} of ${agencies.length} agenc${agencies.length === 1 ? "y" : "ies"} have no bound Telegram chat — they won't be notified`
              : `${agencyNameOf(selected)} has no bound Telegram chat — it won't be notified`}
          </p>
          {selected === BROADCAST && (
            <p className="text-nx-text-3">
              {unboundTargets.map((a) => a.name).join(", ")}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}

// RequestDetailDrawer — the request-detail DELIVERY-MANAGEMENT surface.
//
// Opens over one queue row and fetches GET /admin/account-requests/{id}
// LAZILY (the list shape has no delivered count — this is deliberately not an
// N+1 table fetch). Shows the fulfilment arithmetic, then — while the request
// is assigned/in_progress — the batch Deliver form FRONT AND CENTER
// (POST /admin/account-requests/{id}/deliver; the agency select is limited to
// the ROUTED agencies only; structured 409/422 refusals render verbatim),
// then the delivered rows, each EDITABLE: re-attribute to any agency
// (POST /admin/accounts/{id}/attribution) or unlink from the request
// (POST /admin/accounts/{id}/unlink, confirm-gated). Legacy placeholder rows
// (is_placeholder) are labelled and can only be unlinked.
function RequestDetailDrawer({
  request,
  agencies,
  clientName,
  onOpenChange,
  onDetail,
  onDelivered,
}: {
  request: AccountRequest | null;
  agencies: AgencyRef[];
  clientName: (id: string) => string;
  onOpenChange: (open: boolean) => void;
  onDetail: (id: string, detail: AccountRequestDetail) => void;
  onDelivered: () => void | Promise<void>;
}) {
  const idsFieldId = useId();
  const agencyFieldId = useId();

  const [detail, setDetail] = useState<AccountRequestDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Deliver form.
  const [agencySel, setAgencySel] = useState("");
  const [idsText, setIdsText] = useState("");
  // Auto-grant on link (operator-approved default): append the delivered ids to
  // the client's PROXY allowlist. DEFAULT-ON; unticking sends an EXPLICIT false
  // to opt out (the backend now defaults to append when the field is omitted).
  const [appendAllowlist, setAppendAllowlist] = useState(true);
  const [delivering, setDelivering] = useState(false);
  const [refusal, setRefusal] = useState<DeliverRefusal | null>(null);
  const [successNote, setSuccessNote] = useState<string | null>(null);
  // Reopen (shared -> in_progress). A 409 fully_delivered guard is kept and
  // rendered verbatim.
  const [reopening, setReopening] = useState(false);
  const [reopenGuard, setReopenGuard] = useState<ReopenFullyDelivered | null>(
    null,
  );

  function agencyName(id: string): string {
    return agencies.find((a) => a.id === id)?.name ?? id;
  }

  // Single writer for the drawer detail — keeps the page-level cache (queue
  // Delivered column + mismatch marker) in lockstep with what the drawer shows.
  function updateDetail(next: AccountRequestDetail) {
    setDetail(next);
    if (request) onDetail(request.id, next);
  }

  async function fetchDetail(id: string) {
    setLoading(true);
    setError(null);
    try {
      const d = await getAccountRequestDetail(id);
      setDetail(d);
      onDetail(id, d);
      // Pre-select the single routed (non-declined) agency (backend default).
      const routedAgencies = d.agencies.filter((a) => a.status === "routed");
      setAgencySel(
        routedAgencies.length === 1 ? routedAgencies[0].agency_id : "",
      );
    } catch (err) {
      setError(errMessage(err));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }

  // (Re)load whenever a new request opens; reset the form state.
  useEffect(() => {
    if (!request) return;
    setIdsText("");
    setAppendAllowlist(true);
    setRefusal(null);
    setSuccessNote(null);
    setReopenGuard(null);
    void fetchDetail(request.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id]);

  // Parse one advertiser id per line (commas also accepted); de-duplicated —
  // the backend rejects batch-internal duplicates as malformed input.
  const accountIds = Array.from(
    new Set(
      idsText
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );

  const deliverable =
    detail !== null &&
    (detail.request.status === "assigned" ||
      detail.request.status === "in_progress");
  // Every routing decision for the display chips; the deliverable subset
  // (status "routed", not "declined") drives the fulfilling-agency select.
  const agencyEntries = detail?.agencies ?? [];
  const routed = agencyEntries.filter((a) => a.status === "routed");
  const canDeliver =
    deliverable &&
    accountIds.length > 0 &&
    routed.length > 0 &&
    (routed.length === 1 || agencySel !== "") &&
    !delivering;

  // A shared request whose delivered count is short of qty can be REOPENED
  // (shared -> in_progress) to deliver the shortfall. A 409 fully_delivered
  // guard (delivered >= qty) is rendered verbatim.
  const reopenable =
    detail !== null &&
    detail.request.status === "shared" &&
    detail.total_delivered < detail.qty;

  async function onDeliver() {
    if (!canDeliver || !request || !detail) return;
    setDelivering(true);
    setRefusal(null);
    setSuccessNote(null);
    try {
      const res = await deliverAccounts(request.id, {
        agency_id: agencySel || undefined,
        account_ids: accountIds,
        // Sent as an EXPLICIT boolean: true (the default) appends the delivered
        // ids to the client's proxy allowlist; false opts out. The backend now
        // defaults to append when the field is OMITTED, so opt-out MUST be
        // explicit — never rely on absence.
        append_allowlist: appendAllowlist,
      });
      if (res.ok) {
        setSuccessNote(
          `${res.total_delivered} of ${res.qty} delivered${res.fulfilled ? " — request fulfilled" : ""}${res.allowlist_appended ? " — delivered IDs appended to the proxy allowlist" : ""}`,
        );
        setIdsText("");
        setAppendAllowlist(true);
        toast.success(
          res.fulfilled
            ? "Delivery complete — request fulfilled"
            : "Accounts delivered",
        );
        // Merge the authoritative response: new rows appended, fulfilment
        // arithmetic + the (possibly auto-SHARED) request state adopted.
        updateDetail({
          ...detail,
          request: res.request,
          delivered: [...detail.delivered, ...res.delivered],
          total_delivered: res.total_delivered,
          qty: res.qty,
          fulfilled: res.fulfilled,
        });
        await onDelivered();
      } else {
        setRefusal(res);
      }
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setDelivering(false);
    }
  }

  async function onReopen() {
    if (!request || !detail) return;
    setReopening(true);
    setReopenGuard(null);
    try {
      const res = await reopenAccountRequest(request.id);
      if (res.ok) {
        // shared -> in_progress: adopt the new request state so the deliver
        // form re-appears; the queue refreshes to move the row out of shared.
        updateDetail({ ...detail, request: res.request });
        toast.success("Request reopened — deliver the remaining accounts");
        await onDelivered();
      } else {
        setReopenGuard(res);
      }
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setReopening(false);
    }
  }

  // A delivered row was re-attributed — swap it in place.
  function onRowUpdated(row: AccountRow) {
    if (!detail) return;
    updateDetail({
      ...detail,
      delivered: detail.delivered.map((a) => (a.id === row.id ? row : a)),
    });
  }

  // A delivered row was unlinked — drop it and recompute the arithmetic. The
  // backend does NOT recompute the request status (operator re-delivers).
  function onRowUnlinked(row: AccountRow) {
    if (!detail) return;
    const delivered = detail.delivered.filter((a) => a.id !== row.id);
    const total = Math.max(detail.total_delivered - 1, 0);
    updateDetail({
      ...detail,
      delivered,
      total_delivered: total,
      fulfilled: total >= detail.qty,
    });
  }

  return (
    <Drawer
      open={request !== null}
      onOpenChange={onOpenChange}
      title="Request detail"
      description={
        request
          ? `${request.client_id ? clientName(request.client_id) : "Unassigned"} · ${request.geo || "—"} · ${Math.max(request.qty, 1)}× ${request.request_type}`
          : undefined
      }
    >
      {loading ? (
        <div className="grid gap-2">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not load the request detail</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : detail ? (
        <div className="grid gap-5">
          {/* Fulfilment progress + the (auto-)Shared state */}
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-nx-text tabular-nums">
              {detail.total_delivered}/{detail.qty}
            </span>
            <span className="text-xs text-nx-text-3">delivered</span>
            {detail.fulfilled && <ToneBadge tone="credit">Fulfilled</ToneBadge>}
            <ToneBadge
              tone={detail.request.status === "shared" ? "brand" : "na"}
              className="capitalize"
            >
              {detail.request.status.replace("_", " ")}
            </ToneBadge>
          </div>

          {/* Reopen — a shared but under-delivered request goes back to
              in_progress so the shortfall can be delivered. */}
          {reopenable && (
            <div className="grid gap-2 rounded-lg border border-nx-warn/30 bg-nx-warn-soft/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h3 className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
                    Reopen request
                  </h3>
                  <p className="mt-0.5 text-xs text-nx-text-3">
                    Shared with only {detail.total_delivered} of {detail.qty}{" "}
                    delivered. Reopening returns it to in&nbsp;progress to
                    deliver the shortfall (audited).
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={reopening}
                  onClick={onReopen}
                  className="w-fit shrink-0"
                >
                  {reopening ? "Reopening…" : "Reopen"}
                </Button>
              </div>
              {/* The 409 fully_delivered guard, rendered VERBATIM. */}
              {reopenGuard && (
                <div
                  role="alert"
                  className="grid gap-0.5 rounded-md bg-nx-danger-soft px-3 py-2 text-xs text-nx-danger"
                >
                  <p className="font-medium">
                    conflict — {reopenGuard.reason}
                  </p>
                  <p className="font-mono tabular-nums">
                    delivered: {reopenGuard.delivered} · qty: {reopenGuard.qty}
                  </p>
                  <p>
                    Already fully delivered — unlink the placeholder below,
                    then re-deliver.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Deliver form — the primary action while the state machine allows
              it, so it sits at the TOP of the management surface. */}
          {deliverable && (
            <div className="grid gap-2 rounded-lg border border-nx-brand/30 bg-nx-brand-soft/30 p-3">
              <h3 className="text-xs font-medium tracking-wide text-nx-text-2 uppercase">
                Deliver accounts
              </h3>
              {routed.length === 0 ? (
                <p className="text-xs text-nx-text-3">
                  A delivery needs a routed agency — assign one first.
                </p>
              ) : (
                <>
                  <label
                    htmlFor={agencyFieldId}
                    className="text-xs text-nx-text-3"
                  >
                    Fulfilling agency
                  </label>
                  <NativeSelect
                    id={agencyFieldId}
                    value={agencySel}
                    onChange={(e) => setAgencySel(e.target.value)}
                  >
                    {routed.length > 1 && (
                      <option value="">Choose a routed agency…</option>
                    )}
                    {routed.map((a) => (
                      <option key={a.agency_id} value={a.agency_id}>
                        {agencyName(a.agency_id)}
                      </option>
                    ))}
                  </NativeSelect>
                  <label htmlFor={idsFieldId} className="text-xs text-nx-text-3">
                    Advertiser IDs (one per line or comma-separated)
                  </label>
                  <textarea
                    id={idsFieldId}
                    value={idsText}
                    rows={3}
                    spellCheck={false}
                    placeholder={"7000000000123\n7000000000456"}
                    className="w-full rounded-lg border border-nx-border bg-nx-bg-2 px-2.5 py-1.5 font-mono text-xs text-nx-text outline-none focus-visible:border-nx-brand focus-visible:ring-3 focus-visible:ring-nx-brand/20"
                    onChange={(e) => {
                      setIdsText(e.target.value);
                      setRefusal(null);
                    }}
                  />
                  {/* Security-sensitive opt-in (proxy-access grant) — an
                      explicit checkbox, never a default. */}
                  <label className="flex items-start gap-2 text-xs text-nx-text-2">
                    <input
                      type="checkbox"
                      checked={appendAllowlist}
                      onChange={(e) => setAppendAllowlist(e.target.checked)}
                      className="mt-0.5 accent-nx-brand"
                    />
                    <span>
                      Also append delivered IDs to the client&apos;s proxy
                      allowlist
                      <span className="block text-nx-text-3">
                        Grants the client proxy access to these accounts
                        immediately (audited).
                      </span>
                    </span>
                  </label>
                  <Button
                    size="sm"
                    disabled={!canDeliver}
                    onClick={onDeliver}
                    className="w-fit"
                  >
                    {delivering
                      ? "Delivering…"
                      : `Deliver ${accountIds.length || ""}`.trimEnd()}
                  </Button>
                </>
              )}
            </div>
          )}

          {/* Structured 400/409/422 refusals, rendered VERBATIM — outside the
              deliverable gate so they survive an auto-share state change. */}
          {refusal && (
            <div
              role="alert"
              className="grid gap-0.5 rounded-md bg-nx-danger-soft px-3 py-2 text-xs text-nx-danger"
            >
              <p className="font-medium">
                {refusal.status} {refusal.error}
                {refusal.reason ? ` — ${refusal.reason}` : ""}
              </p>
              {refusal.account_id && (
                <p className="font-mono tabular-nums">
                  account_id: {refusal.account_id}
                </p>
              )}
              {refusal.request_status && (
                <p>request status: {refusal.request_status}</p>
              )}
              {refusal.hint && <p>{refusal.hint}</p>}
            </div>
          )}

          {successNote && (
            <p className="rounded-md bg-nx-credit-soft px-3 py-2 text-xs font-medium text-nx-credit">
              {successNote}
            </p>
          )}

          {/* Delivered accounts — each row manageable (re-attribute / unlink) */}
          <div>
            <h3 className="mb-1.5 text-xs font-medium tracking-wide text-nx-text-2 uppercase">
              Delivered accounts
            </h3>
            {detail.delivered.length === 0 ? (
              <p className="text-xs text-nx-text-3">
                Nothing delivered on this request yet.
              </p>
            ) : (
              <ul className="grid gap-1">
                {detail.delivered.map((a) => (
                  <DeliveredAccountRow
                    key={a.id}
                    account={a}
                    agencies={agencies}
                    agencyName={agencyName}
                    onUpdated={onRowUpdated}
                    onUnlinked={onRowUnlinked}
                  />
                ))}
              </ul>
            )}
          </div>

          {/* Routed agencies */}
          <div>
            <h3 className="mb-1.5 text-xs font-medium tracking-wide text-nx-text-2 uppercase">
              Routed agencies
            </h3>
            {agencyEntries.length === 0 ? (
              <p className="text-xs text-nx-text-3">
                No routed agencies yet — assign one from the queue first.
              </p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {agencyEntries.map((a) => (
                  <span
                    key={a.agency_id}
                    className="inline-flex items-center gap-1"
                  >
                    <ToneBadge tone={a.status === "declined" ? "danger" : "brand"}>
                      {agencyName(a.agency_id)}
                    </ToneBadge>
                    <span
                      className={cn(
                        "text-[0.7rem] font-medium capitalize",
                        a.status === "declined"
                          ? "text-nx-danger"
                          : "text-nx-text-3",
                      )}
                    >
                      {a.status}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Share target — the client's BC id / email for delivery */}
          <div>
            <h3 className="mb-1.5 text-xs font-medium tracking-wide text-nx-text-2 uppercase">
              Share to (BC/email)
            </h3>
            {detail.request.share_target ? (
              <span className="font-mono text-xs break-all text-nx-text">
                {detail.request.share_target}
              </span>
            ) : (
              <span className="text-nx-text-3">—</span>
            )}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

// One delivered account row — account # (or the legacy-placeholder label),
// attributed agency, status, shared date; the re-attribute / unlink affordances
// are the shared AttributionUnlinkControls (used identically by the Accounts
// hub drawer). Placeholder rows (is_placeholder) have no real advertiser id — the
// control offers Unlink only.
function DeliveredAccountRow({
  account,
  agencies,
  agencyName,
  onUpdated,
  onUnlinked,
}: {
  account: AccountRow;
  agencies: AgencyRef[];
  agencyName: (id: string) => string;
  onUpdated: (row: AccountRow) => void;
  onUnlinked: (row: AccountRow) => void;
}) {
  return (
    <li className="rounded-md border border-nx-border bg-nx-bg-2 px-2.5 py-1.5">
      <div className="flex items-center justify-between gap-2">
        {account.is_placeholder ? (
          <ToneBadge tone="warn">Placeholder</ToneBadge>
        ) : (
          <span className="font-mono text-xs text-nx-text tabular-nums">
            {account.account_id}
          </span>
        )}
        <span className="text-xs text-nx-text-3">
          {agencyName(account.agency_id)}
        </span>
        <span className="text-xs text-nx-text-3 capitalize">
          {account.status_account}
        </span>
        {account.shared_at && (
          <span className="font-mono text-xs text-nx-text-3 tabular-nums">
            {account.shared_at}
          </span>
        )}
      </div>

      {account.is_placeholder && (
        <p className="mt-1 text-xs text-nx-warn">
          legacy placeholder — no real account
        </p>
      )}

      <AttributionUnlinkControls
        account={account}
        agencies={agencies}
        agencyName={agencyName}
        onUpdated={onUpdated}
        onUnlinked={onUnlinked}
      />
    </li>
  );
}
